import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Autocomplete,
  Grid,
  Paper,
  Tooltip,
  Button,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import ViewIcon from "@mui/icons-material/Visibility";
import { Download } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, differenceInDays } from "date-fns";
import BaseSelect from "components/Shared/Base/BaseSelect";
import {
  useLazyGetVendorReportQuery,
  useLazyGetDailyVendorReportQuery,
  useLazyGetWeeklyVendorReportQuery,
  useLazyGetMonthlyVendorReportQuery,
  useDownloadVendorReportExcelMutation,
} from "store/services/reportApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";

const VendorReport = () => {
  const { viewEntity } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [sortBy, setSortBy] = useState("shipmentCount:-1");
  const [vendor, setVendor] = useState("all");
  const [timeRange, setTimeRange] = useState("today");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [appliedFromDate, setAppliedFromDate] = useState(null);
  const [appliedToDate, setAppliedToDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [
    fetchVendorReport,
    {
      data: vendorReportData,
      isLoading: isVendorLoading,
      isFetching: isVendorFetching,
    },
  ] = useLazyGetVendorReportQuery();
  const [
    fetchDailyVendorReport,
    {
      data: dailyVendorData,
      isLoading: isDailyVendorLoading,
      isFetching: isDailyVendorFetching,
    },
  ] = useLazyGetDailyVendorReportQuery();
  const [
    fetchWeeklyVendorReport,
    {
      data: weeklyVendorData,
      isLoading: isWeeklyVendorLoading,
      isFetching: isWeeklyVendorFetching,
    },
  ] = useLazyGetWeeklyVendorReportQuery();
  const [
    fetchMonthlyVendorReport,
    {
      data: monthlyVendorData,
      isLoading: isMonthlyVendorLoading,
      isFetching: isMonthlyVendorFetching,
    },
  ] = useLazyGetMonthlyVendorReportQuery();
  const [downloadExcel] = useDownloadVendorReportExcelMutation();

  const [fetchVendors, { data: vendorsData, isLoading: isVendorsLoading }] =
    useLazyGetDdlVendorProfileQuery();

  useEffect(() => {
    const accountId = viewEntity?.entity?.account?._id;
    const entityId = viewEntity?.entity?._id;
    if (accountId && entityId) {
      fetchVendors({ account: accountId, entity: entityId });
    }
  }, [fetchVendors, viewEntity]);

  const vendorOptions = [
    { label: "All Vendors", value: "all" },
    ...(vendorsData?.map((vendor) => ({
      label: vendor.label || vendor.shopName,
      value: vendor.value || vendor._id,
    })) || []),
  ];

  const getDateRange = useCallback((range, from, to) => {
    const today = moment().startOf("day");
    switch (range) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "lastMonth":
        return {
          fromDate: moment()
            .subtract(1, "month")
            .startOf("month")
            .format("MM-DD-YYYY"),
          toDate: moment().endOf("month").format("MM-DD-YYYY"),
        };
      case "custom":
        if (from && to && moment(from).isValid() && moment(to).isValid()) {
          if (differenceInDays(new Date(to), new Date(from)) > 90) {
            setErrorMessage("Date range cannot exceed 90 days.");
            return { fromDate: null, toDate: null };
          }
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage(
          "Invalid date range. Ensure 'To' date is after 'From' date."
        );
        return { fromDate: null, toDate: null };
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  const fetchReportData = useCallback(() => {
    if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
      return;
    }
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      appliedFromDate,
      appliedToDate
    );

    const newParams = new URLSearchParams(searchParams);
    newParams.set("vendor", vendor);
    if (fromDate) newParams.set("fromDate", fromDate);
    else newParams.delete("fromDate");
    if (toDate) newParams.set("toDate", toDate);
    else newParams.delete("toDate");
    setSearchParams(newParams, { replace: true });

    const apiParams = {
      page: currentPage,
      limit: perPage,
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      vendor: vendor !== "all" ? vendor : undefined,
      fromDate,
      toDate,
      sortedBy,
      sortDirection: parseInt(sortDirection),
    };


    let fetchPromise;
    if (timeRange === "today") {
      fetchPromise = fetchDailyVendorReport({ params: apiParams });
    } else if (timeRange === "last7days") {
      fetchPromise = fetchWeeklyVendorReport({ params: apiParams });
    } else if (timeRange === "lastMonth") {
      fetchPromise = fetchMonthlyVendorReport({ params: apiParams });
    } else {
      fetchPromise = fetchVendorReport({ params: apiParams });
    }

    fetchPromise.catch((err) => {
      setErrorMessage(
        err?.data?.message || `Failed to fetch ${timeRange} vendor report`
      );
    });
  }, [
    sortBy,
    getDateRange,
    timeRange,
    appliedFromDate,
    appliedToDate,
    searchParams,
    vendor,
    setSearchParams,
    currentPage,
    perPage,
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    fetchDailyVendorReport,
    fetchWeeklyVendorReport,
    fetchMonthlyVendorReport,
    fetchVendorReport,
  ]);

  const handleDownloadExcel = async () => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      appliedFromDate,
      appliedToDate
    );

    const apiParams = {
      fromDate,
      toDate,
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      vendor: vendor !== "all" ? vendor : undefined,
      sortedBy,
      sortDirection,
      page: 1,
      limit: 10000,
    };

    try {
      const response = await downloadExcel({ params: apiParams }).unwrap();
      if (!response.blob) {
        throw new Error("No blob data returned from server");
      }


      const contentType =
        response.blob.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!contentType.includes("spreadsheetml.sheet")) {
        throw new Error("Invalid content type received from server");
      }

      const url = window.URL.createObjectURL(response.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vendor_Report_${moment().format("YYYYMMDD")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
        "Failed to download Excel file. Check filters or server status."
      );
      console.error("Failed to download Excel:", error);
    }
  };

  useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      fetchReportData();
    }
  }, [fetchReportData, viewEntity?.entity?._id, viewEntity?.entity?.account?._id, sortBy]);


  const columns = [
    {
      name: "Shop Name",
      selector: (row) => row.shopName,
      cell: (row) => row.shopName || "N/A",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      cell: (row) => row.email || "N/A",
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      cell: (row) => row.phone || "N/A",
    },
    {
      name: "Shipment Count",
      selector: (row) => row.shipmentCount,
      cell: (row) => row.shipmentCount || 0,
    },
    {
      name: "Remaining Shipment Count",
      selector: (row) => row.remainingShipmentCount,
      cell: (row) => row.remainingShipmentCount || 0,
    },
    {
      name: "Charge",
      selector: (row) => row.charge,
      cell: (row) => `Rs. ${row.charge?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Pending Charge",
      selector: (row) => row.remainingCharge,
      cell: (row) => `Rs. ${row.remainingCharge?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Cash on Delivery",
      selector: (row) => row.cod,
      cell: (row) => `Rs. ${row.cod?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Remaining COD",
      selector: (row) => row.remainingCod,
      cell: (row) => `Rs. ${row.remainingCod?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Vendor Created Date",
      selector: (row) => row.vendorCreatedAt,
      cell: (row) =>
        row.vendorCreatedAt
          ? moment(row.vendorCreatedAt).format("YYYY-MM-DD")
          : "N/A",
    },
    {
      name: "Details",
      cell: (row) => (
        <Link to={`/app/crm/vendor-profile/${row.vendorId}/detail`}>
          <IconButton color="primary">
            <ViewIcon />
          </IconButton>
        </Link>
      ),
      ignoreRowClick: true,
    },
  ];

  const reportData =
    timeRange === "today"
      ? dailyVendorData
      : timeRange === "last7days"
        ? weeklyVendorData
        : timeRange === "lastMonth"
          ? monthlyVendorData
          : vendorReportData;

  const isLoading =
    isVendorLoading || isDailyVendorLoading || isWeeklyVendorLoading || isMonthlyVendorLoading;
  const isFetching =
    isVendorFetching || isDailyVendorFetching || isWeeklyVendorFetching || isMonthlyVendorFetching;
  const filteredData = reportData?.data?.details || [];
  const summaryData = reportData?.data?.summary || {
    totalShipmentCount: 0,
    totalCharge: 0,
    totalCod: 0,
  };

  const getRangeDisplay = () => {
    switch (timeRange) {
      case "today":
        return "Today";
      case "last7days":
        return "Last 7 Days";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (customFromDate && customToDate) {
          return `Custom (${moment(customFromDate).format(
            "MM/DD/YYYY"
          )} - ${moment(customToDate).format("MM/DD/YYYY")})`;
        }
        return "Custom (Invalid Dates)";
      default:
        return "All";
    }
  };

  const isCustomRangeValid =
    customFromDate &&
    customToDate &&
    moment(customFromDate).isValid() &&
    moment(customToDate).isValid() &&
    moment(customToDate).isSameOrAfter(moment(customFromDate), "day") &&
    differenceInDays(customToDate, customFromDate) <= 90;

  useAppMeta("Vendor Report");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout
        breadcrumbs={[
          { title: "Reports", path: "/app/crm/reports/list" },
          { title: "Vendor Report" },
        ]}
      >
        <BreadcrumbLayout.Paper>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 2,
              pt: 2,
              pb: 1,
              gap: 2,
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h6">
                Vendor Report - {getRangeDisplay()}
              </Typography>

              {/* Right-aligned filters */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {/* Vendor */}
                <Autocomplete
                  size="small"
                  options={vendorOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  value={
                    vendorOptions.find((opt) => opt.value === vendor) || {
                      value: "all",
                      label: "All Vendors",
                    }
                  }
                  onChange={(e, newValue) =>
                    setVendor(newValue?.value || "all")
                  }
                  disableClearable
                  disabled={isVendorsLoading || isLoading || isFetching}
                  sx={{ minWidth: 220 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Vendor" />
                  )}
                />

                {/* Range */}
                <BaseSelect
                  label="Range"
                  value={timeRange}
                  onChange={(e) => {
                    setTimeRange(e.target.value);
                    if (e.target.value !== "custom") {
                      setCustomFromDate(null);
                      setCustomToDate(null);
                      setAppliedFromDate(null);
                      setAppliedToDate(null);
                    }
                  }}
                  options={[
                    { label: "Today", value: "today" },
                    { label: "Last 7 Days", value: "last7days" },
                    { label: "Last Month", value: "lastMonth" },
                    { label: "Custom", value: "custom" },
                  ]}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                  size="small"
                  sx={{ minWidth: 140 }}
                />

                {/* Custom Date Pickers */}
                {timeRange === "custom" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DatePicker
                      label="From"
                      value={customFromDate}
                      onChange={(newValue) => {
                        setCustomFromDate(newValue);
                        if (
                          customToDate &&
                          differenceInDays(customToDate, newValue) > 90
                        ) {
                          setCustomToDate(addDays(newValue, 90));
                          setErrorMessage(
                            "Date range adjusted to maximum 90 days."
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 140 }}
                        />
                      )}
                      maxDate={customToDate || new Date()}
                      disableFuture
                    />
                    <DatePicker
                      label="To"
                      value={customToDate}
                      onChange={(newValue) => {
                        setCustomToDate(newValue);
                        if (
                          customFromDate &&
                          differenceInDays(newValue, customFromDate) > 90
                        ) {
                          setCustomFromDate(addDays(newValue, -90));
                          setErrorMessage(
                            "Date range adjusted to maximum 90 days."
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 140 }}
                        />
                      )}
                      minDate={customFromDate}
                      maxDate={new Date()}
                      disableFuture
                    />
                    <Tooltip
                      title={
                        isCustomRangeValid
                          ? "Apply date range"
                          : "Select a valid range (max 90 days)"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!isCustomRangeValid}
                          onClick={() => {
                            setAppliedFromDate(customFromDate);
                            setAppliedToDate(customToDate);
                          }}
                        >
                          Apply
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                )}

                {/* Sort By */}
                <BaseSelect
                  label="Sort By"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  options={[
                    { label: "Shipment Count (Desc)", value: "shipmentCount:-1" },
                    { label: "Shipment Count (Asc)", value: "shipmentCount:1" },
                    { label: "Charge (Desc)", value: "charge:-1" },
                    { label: "Charge (Asc)", value: "charge:1" },
                    { label: "COD (Desc)", value: "cod:-1" },
                    { label: "COD (Asc)", value: "cod:1" },
                  ]}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                  size="small"
                  sx={{ minWidth: 160 }}
                />

                {/* Download */}
                <IconButton
                  onClick={handleDownloadExcel}
                  color="primary"
                  disabled={isLoading || isFetching || !filteredData.length}
                >
                  <Download />
                </IconButton>
              </Box>
            </Box>
            {/* Summary Section */}
            {isLoading || isFetching ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Paper
                elevation={2}
                sx={{ p: 2, mb: 2 }}
              >

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Shipments
                    </Typography>
                    <Typography variant="h6">
                      {summaryData.totalShipmentCount || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Charge
                    </Typography>
                    <Typography variant="h6">
                      Rs. {(summaryData.totalCharge || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Cash on Delivery
                    </Typography>
                    <Typography variant="h6">
                      Rs. {(summaryData.totalCod || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Box>

          {/* Table Section */}
          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No data available for the selected filters. Try adjusting the
                filters or check data in the database.
              </Typography>
            </Box>
          ) : (
            <AppTable
              columns={columns}
              data={filteredData}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={reportData?.data?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            />
          )}

          <Snackbar
            open={!!errorMessage}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );

};

export default VendorReport;
